import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import { useDispatch } from "react-redux";
import { useIonToast } from "@ionic/react";
import ActivityConfirmModal from "../../pages/Challenges/components/ActivityConfirmModal";
import ActivitySuccessModal from "../../pages/Challenges/components/ActivitySuccessModal";
import { recordChallengeActivity } from "../../redux/actions/challengesActions";
import { resetActivityState } from "../../redux/reducer/challengesSlice";
// The confirm/success modals live under the Challenges page; pull in its styles
// so they render correctly when shown from the notification center / popup.
import "../../pages/Challenges/Challenges.min.css";

/**
 * Shared in-app confirm flow for challenge reminder notifications.
 *
 * Challenge notifications no longer deep-link to /sfide. Instead the popup /
 * notification center calls `ref.open(actionAttributes)` and this component
 * shows the SAME ActivityConfirmModal the Sfide page uses, then records the
 * activity via the real confirm endpoint the backend embedded in the action
 * (`confirm_url` / `confirm_verb`). The backend enforces cutoff / expiry /
 * already-confirmed, so a stale confirm surfaces as an error toast.
 *
 * The backend marks the action `disabled` (not `confirmable`) once the day is
 * confirmed or its window has passed, so callers should not open this for a
 * disabled action — but we still guard on the confirm endpoint being present.
 *
 * @param {() => void} [onConfirmed] called after a successful confirm so the
 *   caller can refresh its notification list (button becomes disabled).
 */
const ChallengeNotificationConfirm = forwardRef(({ onConfirmed }, ref) => {
  const dispatch = useDispatch();
  const [presentToast] = useIonToast();

  const [challenge, setChallenge] = useState(null);
  const [confirmAttrs, setConfirmAttrs] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = useCallback((attrs) => {
    const a = attrs?.attributes || attrs || {};
    const challengeId = a.challenge_id ?? a.challengeId;
    // Confirmation posts directly to the challenge now (no push_notification_action),
    // so only the challenge id is required — a confirm_url is no longer needed.
    if (!challengeId) return false;
    const activityDate = a.activity_date ?? a.activityDate ?? null;
    // Build the minimal challenge shape ActivityConfirmModal / SuccessModal need.
    setChallenge({
      id: challengeId,
      attributes: {
        title: a.challenge_title ?? a.challengeTitle ?? "Sfida",
        reward_points: a.reward_points ?? a.rewardPoints,
        participation: {
          attributes: { pending_activity_date: activityDate },
        },
      },
    });
    setConfirmAttrs({ challengeId, activityDate });
    setConfirmOpen(true);
    return true;
  }, []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    setChallenge(null);
    setConfirmAttrs(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!confirmAttrs || loading) return;
    setLoading(true);
    try {
      const res = await dispatch(
        recordChallengeActivity({
          challengeId: confirmAttrs.challengeId,
          activityDate: confirmAttrs.activityDate,
        }),
      ).unwrap();
      setResult(res);
      setConfirmOpen(false);
      setSuccessOpen(true);
      onConfirmed?.();
    } catch (err) {
      // Backend enforces cutoff / expiry / already-confirmed — surface its
      // Italian message (e.g. "Hai già confermato l'attività per questa
      // giornata.").
      presentToast({
        message:
          typeof err === "string"
            ? err
            : err?.message || "Impossibile confermare l'attività. Riprova.",
        duration: 2800,
        color: "danger",
      });
      closeConfirm();
    } finally {
      setLoading(false);
      dispatch(resetActivityState());
    }
  }, [confirmAttrs, loading, dispatch, presentToast, onConfirmed, closeConfirm]);

  const closeSuccess = useCallback(() => {
    setSuccessOpen(false);
    setResult(null);
    setChallenge(null);
    setConfirmAttrs(null);
  }, []);

  return (
    <>
      <ActivityConfirmModal
        isOpen={confirmOpen}
        onClose={closeConfirm}
        challenge={challenge}
        onConfirm={handleConfirm}
        isLoading={loading}
      />
      <ActivitySuccessModal
        isOpen={successOpen}
        onClose={closeSuccess}
        result={result}
        challenge={challenge}
      />
    </>
  );
});

ChallengeNotificationConfirm.displayName = "ChallengeNotificationConfirm";

export default ChallengeNotificationConfirm;
